import { FollowArtistButton } from '@app/pages/i/components/FollowArtistButton/FollowArtistButton';
import { useFollowArtist } from '@app/pages/i/hooks/useFollowArtist';
import {
  Avatar,
  Box,
  constants,
  PageSection,
  Text,
} from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import React from 'react';
import { ArtistAchievements } from './ArtistAchievements';
import { ArtistExternalLinks } from './ArtistExternalLinks';
import { ArtistMetrics } from './ArtistMetrics';
import styles from './ArtistProfile.module.scss';
import { ArtistProfileProps } from './types';

const messages = defineMessages({
  about: {
    defaultMessage: 'About {displayName}',
  },
  totalWorks: {
    defaultMessage: `{worksCount, plural,
       =0 {0 designs}
       one {1 design}
       other {{worksCount} designs}}`,
  },
  returnPolicy: {
    defaultMessage: 'Return policy',
  },
  businessNumber: {
    defaultMessage: 'Business Phone: {businessPhoneNumber}',
  },
});

const RETURNS_HELP_CENTRE_ARTICLE = '5857691382164';

export const ArtistProfile: React.FC<ArtistProfileProps> = ({
  artistInfo,
  badgeStatuses,
  isFollowing,
}) => {
  const intl = useIntl();
  const [{ isLoggedIn }] = useUserInfo<UserInfo>();
  const {
    id,
    avatar,
    shopDisplayName,
    username,
    additionalInfo,
    worksCount,
    bio,
    externalSites,
    metrics,
    usBusinessInfo,
  } = artistInfo;

  const displayName = shopDisplayName ?? username;

  const { followingArtist, handleOnToggleFollowArtist } = useFollowArtist({
    isLoggedIn,
    artistId: id,
    initialFollowingStatus: isFollowing,
  });

  return (
    <PageSection size={constants.MEDIUM} paddingTop="xl" paddingBottom="xl">
      <Box
        id="profile"
        className={styles.artistProfile}
        paddingLeft="l"
        paddingRight="l"
      >
        <Box className={styles.header}>
          {avatar && (
            <Box
              className={styles.avatar}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Avatar src={avatar} size={constants.LARGE} />
            </Box>
          )}
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            className={styles.about}
          >
            <Box className={styles.aboutDisplayName}>
              <Text type="display2">
                {intl.formatMessage(messages.about, { displayName })}
              </Text>
            </Box>
            <Box className={styles.artistInfoList} element="ul">
              <Text
                muted
                type="display6"
                element="li"
                className={styles.artistInfoItem}
              >
                {additionalInfo}
              </Text>
              {worksCount !== undefined && worksCount > 0 && (
                <Text
                  muted
                  type="display6"
                  element="li"
                  className={styles.artistInfoItem}
                >
                  {intl.formatMessage(messages.totalWorks, { worksCount })}
                </Text>
              )}
            </Box>
            {usBusinessInfo.shouldDisplayInformation &&
              usBusinessInfo.businessName &&
              usBusinessInfo.businessAddress &&
              usBusinessInfo.businessPhoneNumber && (
                <Box className={styles.businessInfo}>
                  <Text muted type="caption">
                    {usBusinessInfo.businessName},{' '}
                  </Text>
                  <Text muted type="caption">
                    {intl.formatMessage(messages.businessNumber, {
                      businessPhoneNumber: usBusinessInfo.businessPhoneNumber,
                    })}
                  </Text>
                  <Text muted type="caption" display="block">
                    {usBusinessInfo.businessAddress}
                  </Text>
                </Box>
              )}
          </Box>
          <Box
            className={styles.actions}
            paddingTop="l"
            display="flex"
            flexDirection="row"
          >
            <FollowArtistButton
              followingArtist={followingArtist}
              handleOnToggleFollowArtist={handleOnToggleFollowArtist}
              longLabel
            />
          </Box>
        </Box>

        <ArtistMetrics
          username={username}
          followerCount={metrics.followerCount}
          followingCount={metrics.followingCount}
          favoriteReceivedCount={metrics.favoriteReceivedCount}
        />

        {bio && (
          <Box
            className={styles.bioText}
            paddingTop="m"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: noDangerouslySetInnerHtml
            dangerouslySetInnerHTML={{ __html: bio }}
          />
        )}

        {usBusinessInfo.shouldDisplayInformation && (
          <Box marginTop="s">
            <a
              className={styles.returnPolicyLink}
              href={`https://help.redbubble.com/hc/${intl.locale}/articles/${RETURNS_HELP_CENTRE_ARTICLE}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage(messages.returnPolicy)}
            </a>
          </Box>
        )}

        <ArtistAchievements badgeStatuses={badgeStatuses} />

        <ArtistExternalLinks externalSites={externalSites} />
      </Box>
    </PageSection>
  );
};
